import { useState, type ChangeEvent } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { PermissionGate } from '../PermissionGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { saveCredential, VIA_FOLDER, VIA_SFTP } from './credentialsApi';

/**
 * Login i hasło serwera, na który idzie plik zlecenia do importu TMS —
 * SFTP (15.10.2026) albo folder sieciowy (28.10.2026). Jeden formularz,
 * dwa zestawy kluczy w magazynie.
 *
 * **Tylko zapis.** Pola nigdy nie pokazują zapisanej wartości — magazyn jej
 * nie oddaje, a w tabeli niżej stoi maska. Puste pole znaczy „bez zmian”,
 * więc zmiana samego hasła nie wymaga przepisywania loginu.
 *
 * Poświadczenie wspólne dla instancji (bez spółki): serwer importu jest
 * jeden dla obu spółek, spółkę niesie treść pliku. Adres, katalog
 * (`preprod` / `prod`) i reszta to konfiguracja wdrożenia (env), nie ten
 * ekran. O dostępie rozstrzyga API (zarządzanie kontami).
 */
interface FileServerCredentialsFormProps {
  system: string;
  testId: string;
  heading: string;
  /** Początek opisu wpisu w magazynie („… — login”). */
  description: string;
  footnote: string;
}

const FileServerCredentialsForm = ({
  system,
  testId,
  heading,
  description,
  footnote,
}: FileServerCredentialsFormProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (login.trim() !== '') {
        await saveCredential({
          system,
          field: VIA_SFTP.LOGIN,
          value: login.trim(),
          companyId: null,
          description: `${description} — login`,
        });
      }
      if (password !== '') {
        await saveCredential({
          system,
          field: VIA_SFTP.PASSWORD,
          value: password,
          companyId: null,
          description: `${description} — hasło`,
        });
      }
      setLogin('');
      setPassword('');
      notify('Zapisano w magazynie poświadczeń — wartości nie da się tu odczytać', {
        type: 'success',
      });
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionGate permission="MANAGE_USERS">
      <Box data-testid={testId} sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {heading}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Nazwa użytkownika"
            value={login}
            autoComplete="off"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLogin(e.target.value)}
          />
          <TextField
            size="small"
            label="Hasło"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || (login.trim() === '' && password === '')}
            onClick={() => void save()}
          >
            Zapisz w magazynie
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {footnote}
        </Typography>
      </Box>
    </PermissionGate>
  );
};

/** Serwer SFTP importu TMS (15.10.2026). */
export const ViaSftpCredentialsForm = () => (
  <FileServerCredentialsForm
    system={VIA_SFTP.SYSTEM}
    testId="via-sftp-credentials"
    heading="Serwer SFTP importu TMS (wspólny dla obu spółek):"
    description="Serwer SFTP importu TMS"
    footnote="Pola nie pokazują zapisanych wartości; puste pole zostawia dotychczasową. Adres serwera, katalog (preprod / prod) i odcisk klucza ustawia konfiguracja wdrożenia."
  />
);

/** Folder sieciowy importu TMS (28.10.2026; wysyłka działa tylko na produkcji). */
export const ViaFolderCredentialsForm = () => (
  <FileServerCredentialsForm
    system={VIA_FOLDER.SYSTEM}
    testId="via-folder-credentials"
    heading="Folder sieciowy importu TMS (wspólny dla obu spółek):"
    description="Folder sieciowy importu TMS"
    footnote="Pola nie pokazują zapisanych wartości; puste pole zostawia dotychczasową. Adres serwera, nazwę folderu i katalog (preprod / prod) ustawia konfiguracja wdrożenia; wysyłka do folderu działa tylko na produkcji."
  />
);
